import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Facebook, Instagram, Youtube } from 'lucide-react';
import { cdnAsset, cdnFolder } from '../utils/cdn';
const logo = cdnAsset('logo.svg');
const terms = cdnAsset('terms.pdf');

const getDiplomaImage = cdnFolder('images');

const Footer = () => {
  const navigate = useNavigate();
  const [showDiplomasModal, setShowDiplomasModal] = useState(false);
  const [diplomaImageIndex, setDiplomaImageIndex] = useState(0);
  const [mobileSectionsOpen, setMobileSectionsOpen] = useState({
    jewelry: true,
    about: false,
    contact: false,
    general: false,
  });
  
  const diplomaImages = [
    { src: getDiplomaImage('diplomas_1.jpeg'), alt: 'Diploma 1' },
    { src: getDiplomaImage('diplomas_2.jpeg'), alt: 'Diploma 2' }
  ];

  const toggleMobileSection = (sectionKey) => {
    setMobileSectionsOpen((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const scrollToSection = useCallback((sectionId) => {
    if (window.location.pathname === '/about') {
      // Already on about page — find the visible element (duplicate IDs for mobile/tablet/desktop)
      const elements = document.querySelectorAll(`[id="${sectionId}"]`);
      for (const el of elements) {
        if (el.offsetParent !== null || el.getClientRects().length > 0) {
          const top = el.getBoundingClientRect().top + window.pageYOffset - 80;
          window.scrollTo({ top, behavior: 'smooth' });
          return;
        }
      }
    } else {
      // Navigate to about page with state (not hash) to avoid browser auto-scroll
      navigate('/about', { state: { scrollTo: sectionId } });
    }
  }, [navigate]);

  return (
    <footer className="bg-white text-[#455A6B] py-16 lg:py-[48px]">
      {/* Mobile footer */}
      <div className="sm:hidden max-w-[520px] mx-auto px-[24px] text-center">
        <div className="flex flex-col items-center text-center">
          <img src={logo} alt="SDNY Logo" className="h-[120px] w-auto" />
          <h3 className="text-[22px] mt-4 text-black">Simon Diamond</h3>
          <p className="text-black font-light text-[16px]">New York</p>
          <p className="font-medium text-[#455A6B] text-[13px] mt-1">© 2026 Simon Diamond New York</p>
        </div>

        <div className="mt-8 space-y-4 text-[#455A6B] text-[13px]">
          <div className="pb-3 border-b border-gray-200">
            <button
              type="button"
              className="mx-auto flex w-full max-w-[220px] items-center justify-center gap-2 text-black font-medium text-[14px]"
              onClick={() => toggleMobileSection('jewelry')}
              aria-expanded={mobileSectionsOpen.jewelry}
            >
              <span>Jewelry</span>
              <span
                className={`text-[16px] transition-transform duration-200 ${
                  mobileSectionsOpen.jewelry ? 'rotate-180' : ''
                }`}
              >
                ^
              </span>
            </button>
            <div
              className={`transition-all duration-300 ease-out overflow-hidden ${
                mobileSectionsOpen.jewelry ? 'max-h-96 opacity-100 mt-2' : 'max-h-0 opacity-0'
              }`}
            >
              <ul className="space-y-1.5 text-center">
                <li><Link to="/products/engagement_rings" className="hover:underline">Engagement Rings</Link></li>
                <li><Link to="/products/finished__annivers" className="hover:underline">Anniversary Bands</Link></li>
                <li><Link to="/products/WB" className="hover:underline">Wedding Bands</Link></li>
                <li><Link to="/products/finished__eternity" className="hover:underline">Eternity Bands</Link></li>
                <li><Link to="/products/finished__pendant__classic" className="hover:underline">Pendants</Link></li>
                <li><Link to="/remake" className="hover:underline">AI Jewelry Builder</Link></li>
              </ul>
            </div>
          </div>

          <div className="pb-3 border-b border-gray-200">
            <button
              type="button"
              className="mx-auto flex w-full max-w-[220px] items-center justify-center gap-2 text-black font-medium text-[14px]"
              onClick={() => toggleMobileSection('about')}
              aria-expanded={mobileSectionsOpen.about}
            >
              <span>About</span>
              <span
                className={`text-[16px] transition-transform duration-200 ${
                  mobileSectionsOpen.about ? 'rotate-180' : ''
                }`}
              >
                ^
              </span>
            </button>
            <div
              className={`transition-all duration-300 ease-out overflow-hidden ${
                mobileSectionsOpen.about ? 'max-h-96 opacity-100 mt-2' : 'max-h-0 opacity-0'
              }`}
            >
              <ul className="space-y-1.5 text-center">
                <li><button onClick={() => scrollToSection('legacy')} className="hover:underline">Our Legacy</button></li>
                <li><button onClick={() => scrollToSection('founder')} className="hover:underline">Our Founder</button></li>
                <li>
                  <button 
                    onClick={() => setShowDiplomasModal(true)}
                    className="text-center hover:underline"
                  >
                    Certifications
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="pb-3 border-b border-gray-200">
            <button
              type="button"
              className="mx-auto flex w-full max-w-[220px] items-center justify-center gap-2 text-black font-medium text-[14px]"
              onClick={() => toggleMobileSection('contact')}
              aria-expanded={mobileSectionsOpen.contact}
            >
              <span>Contact</span>
              <span
                className={`text-[16px] transition-transform duration-200 ${
                  mobileSectionsOpen.contact ? 'rotate-180' : ''
                }`}
              >
                ^
              </span>
            </button>
            <div
              className={`transition-all duration-300 ease-out overflow-hidden ${
                mobileSectionsOpen.contact ? 'max-h-96 opacity-100 mt-2' : 'max-h-0 opacity-0'
              }`}
            >
              <ul className="space-y-1.5 text-center">
                <li><a href="tel:+16463021164" className="hover:underline">Call Us</a></li>
                <li><a href="mailto:info@sdnyusa.com" className="hover:underline">Mail Us</a></li>
                {/* <li><Link to="/locations" className="hover:underline">Locations</Link></li> */}
              </ul>
            </div>
          </div>

          <div className="pb-3 border-b border-gray-200">
            <button
              type="button"
              className="mx-auto flex w-full max-w-[220px] items-center justify-center gap-2 text-black font-medium text-[14px]"
              onClick={() => toggleMobileSection('general')}
              aria-expanded={mobileSectionsOpen.general}
            >
              <span>General</span>
              <span
                className={`text-[16px] transition-transform duration-200 ${
                  mobileSectionsOpen.general ? 'rotate-180' : ''
                }`}
              >
                ^
              </span>
            </button>
            <div
              className={`transition-all duration-300 ease-out overflow-hidden ${
                mobileSectionsOpen.general ? 'max-h-96 opacity-100 mt-2' : 'max-h-0 opacity-0'
              }`}
            >
              <ul className="space-y-1.5 text-center">
                <li><button onClick={() => open(terms)} className="hover:underline">Terms &amp; Conditions</button></li>
                {/* <li><Link to="/cookies" className="hover:underline">Cookie Policy</Link></li> */}
                {/* <li><Link to="/faqs" className="hover:underline">FAQs</Link></li> */}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop footer */}
      <div className="hidden sm:block max-w-[1440px] mx-auto px-[104px]">
        {/* Main grid: logo/brand on left, 4 columns on right */}
        <div className="flex flex-col justify-between gap-8 lg:flex-row lg:gap-8">
          
          {/* Left: Brand block */}
          <div className="flex flex-col items-center text-center">
            <img src={logo} alt="SDNY Logo" className="h-[120px] w-auto" />
            <h3 className="text-[24px] mt-4 text-black">Simon Diamond</h3>
            <p className="text-black font-light text-[18px]">New York</p>
            <p className="font-medium text-[#455A6B] text-[15px]">© 2026 Simon Diamond New York</p>
          </div>

          {/* Right: 4 columns */}
          <div className="grid grid-cols-2 gap-6 lg:grid-cols-4 lg:gap-8">
          {/* Jewelry */}
          <div className="text-right">
            <span className="mb-4 text-base font-medium text-black">Jewelry</span>
            <ul className="mt-2 space-y-2 text-sm" >
              <li><Link to="/products/engagement_rings" className="hover:underline">Engagement Rings</Link></li>
              <li><Link to="/products/finished__annivers" className="hover:underline">Anniversary Bands</Link></li>
              <li><Link to="/products/WB" className="hover:underline">Wedding Bands</Link></li>
              <li><Link to="/products/finished__eternity" className="hover:underline">Eternity Bands</Link></li>
              <li><Link to="/products/finished__pendant__classic" className="hover:underline">Pendants</Link></li>
              {/* <li><Link to="/categories" className="hover:underline">Custom Ring Builder</Link></li> */}
              <li><Link to="/remake" className="hover:underline">AI Jewelry Builder</Link></li>
            </ul>
          </div>

          {/* About */}
          <div className="text-right">
            <span className="mb-4 text-base font-medium text-black">About</span>
            <ul className="mt-2 space-y-2 text-sm">
              {/* <li><Link to="/reviews" className="hover:underline">Reviews &amp; Feedback</Link></li> */}
              <li><button onClick={() => scrollToSection('legacy')} className="text-right hover:underline">Our Legacy</button></li>
              <li><button onClick={() => scrollToSection('founder')} className="text-right hover:underline">Our Founder</button></li>
              {/* <li><Link to="/education" className="hover:underline">Education</Link></li> */}
              <li>
                <button 
                  onClick={() => setShowDiplomasModal(true)}
                  className="text-left hover:underline"
                >
                  Certifications
                </button>
              </li>
              {/* <li><Link to="/blogs" className="hover:underline">Blogs</Link></li> */}
               {/* <li><Link to="/warranty" className="hover:underline">Warranty</Link></li> */}
            </ul>
          </div>

          {/* Contact */}
          <div className="text-right">
            <span className="mb-4 text-base font-medium text-black">Contact</span>
            <ul className="mt-2 space-y-2 text-sm">
              <li><a href="tel:+16463021164" className="hover:underline">Call Us</a></li>
              <li><a href="mailto:info@sdnyusa.com" className="hover:underline">Mail Us</a></li>
              {/* <li><Link to="/locations" className="hover:underline">Locations</Link></li> */}
              {/* <li><Link to="/returns" className="hover:underline">Returns</Link></li> */}
            </ul>

            </div>
            
             {/* General */}
             <div className="text-right">
            <span className="mb-4 text-base font-medium text-black">General</span>
            <ul className="mt-2 space-y-2 text-sm">
              <li><button onClick={() => open(terms)} className="hover:underline">Terms &amp; Conditions</button></li>
              {/* <li><Link to="/cookies" className="hover:underline">Cookie Policy</Link></li> */}
              {/* <li><Link to="/faqs" className="hover:underline">FAQs</Link></li> */}
              {/* <li><Link to="/legal" className="hover:underline">Legal</Link></li> */}
            </ul>
          </div>

            {/* Social icons */}
            {/* <div className="flex items-center gap-4 mt-6">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-[#455A6B] hover:text-black">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-[#455A6B] hover:text-black">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.5 8h4V23h-4V8zm7.5 0h3.8v2.05h.05c.53-1 1.83-2.05 3.77-2.05 4.03 0 4.78 2.65 4.78 6.1V23h-4v-5.2c0-1.24-.02-2.83-1.73-2.83-1.73 0-2 1.35-2 2.74V23h-4V8z"/></svg>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-[#455A6B] hover:text-black">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="mailto:info@sdnyusa.com" aria-label="Email" className="text-[#455A6B] hover:text-black">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 13L2 6h20l-10 7zm-10 5V8l10 7 10-7v10H2z"/></svg>
              </a>
            </div> */}
          </div>
        </div>
      </div>

      {/* Diplomas Modal */}
      {showDiplomasModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[200] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 flex items-center justify-between p-6 bg-white border-b border-gray-200">
              <h2 className="text-2xl font-semibold text-gray-900">Gemological Diplomas</h2>
              <button 
                onClick={() => setShowDiplomasModal(false)}
                className="text-gray-500 transition hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body - Image Viewer */}
            <div className="p-8 bg-gray-50">
              <div className="mb-6 overflow-hidden bg-white rounded-lg">
                <img 
                  src={diplomaImages[diplomaImageIndex].src}
                  alt={diplomaImages[diplomaImageIndex].alt}
                  className="object-cover w-full h-auto"
                />
              </div>

              {/* Navigation Controls */}
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setDiplomaImageIndex(Math.max(0, diplomaImageIndex - 1))}
                  disabled={diplomaImageIndex === 0}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 transition bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Previous
                </button>

                <span className="text-sm text-gray-600">
                  {diplomaImageIndex + 1} of {diplomaImages.length}
                </span>

                <button
                  onClick={() => setDiplomaImageIndex(Math.min(diplomaImages.length - 1, diplomaImageIndex + 1))}
                  disabled={diplomaImageIndex === diplomaImages.length - 1}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 transition bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowDiplomasModal(false)}
                className="w-full py-3 font-medium text-white transition bg-gray-800 rounded-lg hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;
